<!-- /resources/js/components/Sections/Product/CatalogBetonSpecPicker.vue -->
<script setup>
import {computed} from 'vue'
import {useCartStore} from '@js/store/CartStore.js'
import IconBan from '@js/components/Icons/IconBan.vue'

const props = defineProps({
    productId: { type: Number, required: true },
    specs: { type: Array, default: () => [] }, // уже нормализованные или сырые
})

const cart = useCartStore()

// Если specs пришли «сырыми», можно подстраховаться и нормализовать:
const normalized = computed(() => {
    const hasNormalizer = typeof cart._normalizeSpecs === 'function'
    return hasNormalizer ? cart._normalizeSpecs(props.specs) : (props.specs || [])
})

const sel = computed(() => cart.getSelection(props.productId))

const hasGost = computed(() => normalized.value.some(s => s.type === 'GOST'))
const hasTu   = computed(() => normalized.value.some(s => s.type === 'TU'))

const hasPolypropylene = computed(() => normalized.value.some(s => s.fiber_polypropylene))
const hasSteel         = computed(() => normalized.value.some(s => s.fiber_steel))
const hasFiber         = computed(() => hasPolypropylene.value || hasSteel.value)

const pmdAvailable = computed(() => {
    const fields = ['price_05','price_10','price_15','price_20','price_25']
    const set = new Set()
    for (const s of normalized.value) {
        for (const f of fields) {
            const v = Number(String(s[f] ?? 0))
            if (Number.isFinite(v) && v > 0) set.add(f)
        }
    }
    return Array.from(set)
})

const setType  = (v) => cart.setSelection(props.productId, { type: v })
const setPMD   = (v) => cart.setSelection(props.productId, { price_type: v }) // null => без ПМД
const setFiber = (v) => cart.setSelection(props.productId, { fiber: v })
</script>

<template>
    <div v-if="normalized.length" class="flex  flex-wrap gap-3 text-[11px]">
        <!-- Сертификация -->
        <div class="flex flex-col gap-1.5">
            <span class="font-semibold">Сертификация:</span>
            <div class="flex gap-1">
                <button @click="setType(null)"
                        class="border rounded w-7 h-7 p-[3px] cursor-pointer border-gray-400"
                        :class="[sel.type===null ? 'bg-baseColor' : '']" title="Без выбора">
                    <IconBan class="w-full h-full"/>
                </button>
                <button v-if="hasGost" @click="setType('GOST')"
                        class="border rounded w-7 h-7 p-[1px] cursor-pointer border-gray-400"
                        :class="[sel.type==='GOST' ? 'bg-baseColor' : '']" title="ГОСТ">
                    <img class="w-full h-full object-contain" src="@img/gost/gost.webp" alt="gost"/>
                </button>
                <button v-if="hasTu" @click="setType('TU')"
                        class="border rounded w-7 h-7 p-[1px] cursor-pointer border-gray-400"
                        :class="[sel.type==='TU' ? 'bg-baseColor' : '']" title="ТУ">
                    <img class="w-full h-full object-contain" src="@img/gost/tu.webp" alt="tu"/>
                </button>
            </div>
        </div>

        <!-- ПМД -->
        <div class="flex flex-col gap-1.5">
            <span class="font-semibold">ПМД:</span>
            <div class="flex gap-1">
                <button @click="setPMD(null)"
                        class="border rounded w-7 h-7 p-[3px] cursor-pointer border-gray-400"
                        :class="[sel.price_type===null ? 'bg-baseColor' : '']" title="Без ПМД">
                    <IconBan class="w-full h-full"/>
                </button>
                <button v-for="field in pmdAvailable" :key="field"
                        @click="setPMD(field)"
                        class="border rounded w-7 h-7 cursor-pointer flex items-center justify-center border-gray-400"
                        :class="[sel.price_type===field ? 'bg-baseColor' : '']"
                        :title="field">
                    <span class="font-semibold text-[10px]">
                        {{ {price_05:'−5',price_10:'−10',price_15:'−15',price_20:'−20',price_25:'−25'}[field] }}°C
                    </span>
                </button>
            </div>
        </div>

        <!-- Фибра -->
        <div v-if="hasFiber" class="flex flex-col gap-1.5">
            <span class="font-semibold">Фибра:</span>
            <div class="flex gap-1">
                <button @click="setFiber(null)"
                        class="border rounded w-7 h-7 p-[3px] cursor-pointer border-gray-400"
                        :class="[sel.fiber===null ? 'bg-baseColor' : '']" title="Без фибры">
                    <IconBan class="w-full h-full"/>
                </button>
                <button v-if="hasPolypropylene"
                        @click="setFiber('fiber_polypropylene')"
                        class="border rounded w-7 h-7 cursor-pointer"
                        :class="[sel.fiber==='fiber_polypropylene' ? 'border-baseColor' : 'border-gray-400']"
                        title="Полипропиленовая">
                    <img class="w-full h-full object-contain" src="@img/fibra/polipropelen.webp" alt="poly"/>
                </button>
                <button v-if="hasSteel"
                        @click="setFiber('fiber_steel')"
                        class="border rounded w-7 h-7 cursor-pointer"
                        :class="[sel.fiber==='fiber_steel' ? 'border-baseColor' : 'border-gray-400']"
                        title="Стальная">
                    <img class="w-full h-full object-contain" src="@img/fibra/steel.webp" alt="steel"/>
                </button>
            </div>
        </div>
    </div>
</template>
